import { ProgramsSideBarDatamodel } from './programs-side-bar-datamodel';
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../stores/global-settings-branches';
import { getHebrewYear } from '../../../utils/hebrew-date-utils';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { CustomTag } from '../../../components/CustomTag/CustomTag';
import { CustomExpander } from '../../../components/CustomExpander/CustomExpander';
import { FilterExpanderWithListOfCheckboxes } from '../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import {
  ProgramStudentStatus,
  getProgramStudentStatusDisplayText,
} from '../../../api/types/programs/students/program-student-status';
import { ProgramStatus } from '../../../api/types/programs/program-status';

interface YearOption {
  year: string;
  value: number;
}

interface StatusOption {
  status: ProgramStatus;
  severity: 'success' | 'warning' | 'danger' | 'info';
  checked: boolean;
}

export interface ProgramsSideBarProps {
  onSearch: (filters: ProgramsSideBarDatamodel) => void;
}

export const ProgramsSideBar: React.FC<ProgramsSideBarProps> = observer(
  ({ onSearch }) => {
    const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [statuses, setStatuses] = useState<StatusOption[]>([
      {
        status: ProgramStatus.ACTIVE,
        severity: 'success',
        checked: false,
      },
      {
        status: ProgramStatus.INIT,
        severity: 'info',
        checked: false,
      },
      {
        status: ProgramStatus.FINISHED,
        severity: 'success',
        checked: false,
      },
      {
        status: ProgramStatus.CLOSE,
        severity: 'danger',
        checked: false,
      },
      /*{ status: ProgramStudentStatus.LEFT, severity: 'danger', checked: false },
      {
        status: ProgramStudentStatus.CANCELLED,
        severity: 'danger',
        checked: false,
      },*/
    ]);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        value: year,
      });
    }

    const onStatusChange = (index: number) => {
      const newStatuses = [...statuses];
      newStatuses[index].checked = !newStatuses[index].checked;
      setStatuses(newStatuses);
    };

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranches((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const handleYearToggle = (yearStr: string) => {
      const year = parseInt(yearStr);
      setSelectedYears((prev) =>
        prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
      );
    };

    const handleSearch = () => {
      const filters: ProgramsSideBarDatamodel = {
        branchesIds: selectedBranches,
        startYears: selectedYears,
        programsStatuses: statuses
          .filter((s) => s.checked)
          .map((s) => s.status),
      };
      onSearch(filters);
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      statusContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px 0',
      },
      statusItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        direction: 'rtl' as const,
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>סינונים</div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranches}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={handleYearToggle}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          <CustomExpander badge={statuses.filter((s) => s.checked).length}>
            <div>סטטוס תוכנית</div>
            <div style={styles.statusContainer}>
              {statuses.map((status, index) => (
                <div key={status.status} style={styles.statusItem}>
                  <Checkbox
                    checked={status.checked}
                    onChange={() => onStatusChange(index)}
                  />
                  <CustomTag
                    width={'70px'}
                    text={ProgramStatus.getDisplayText(status.status)}
                    severity={status.severity}
                  />
                </div>
              ))}
            </div>
          </CustomExpander>
        </div>
        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  },
);
